/**
 * Cluster-shared IndexedDB persistence.
 *
 * One DB ('disk-image-cluster' v1) with four object stores so all four tools
 * share state without colliding:
 *   - extractor_state    keyed by 'current'   (ISO browse + extraction queue)
 *   - creator_drafts     keyed by 'current'   (creator's last folder selection + settings)
 *   - inspector_history  autoincrement        (recent inspections, metadata only)
 *   - cluster_kv         keyed by string      (settings, gate cache, handoff payloads)
 *
 * TTL: 7 days for resume sessions, 30 days for inspector_history. Pruning
 * runs lazily on each .open() — fire-and-forget.
 *
 * Save calls debounce 600 ms per (store, key) pair (mirrors ocr-core pattern).
 * Failures are swallowed (private-browsing, quota, ...) — never blocks tool work.
 *
 * Surface: window.__DiskImage.Persistence
 *   await Persistence.open()
 *   await Persistence.put(store, key, value)             // debounced
 *   await Persistence.putNow(store, key, value)          // immediate
 *   await Persistence.get(store, key)
 *   await Persistence.delete(store, key)
 *   await Persistence.append('inspector_history', record)
 *   await Persistence.list('inspector_history', limit)
 *   await Persistence.clearStore(store)
 *   await Persistence.clearAll()
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.Persistence) return;

    var DB_NAME = 'disk-image-cluster';
    var DB_VERSION = 1;
    var STORES = ['extractor_state', 'creator_drafts', 'inspector_history', 'cluster_kv'];
    var TTL_RESUME = 7 * 24 * 60 * 60 * 1000;
    var TTL_HISTORY = 30 * 24 * 60 * 60 * 1000;
    var HISTORY_MAX = 50;
    var DEBOUNCE_MS = 600;

    var _db = null;
    var _opening = null;
    var _supported = !!w.indexedDB;
    var _debouncers = new Map(); // key → timer

    function _open() {
        if (!_supported) return Promise.reject(new Error('no-idb'));
        if (_db) return Promise.resolve(_db);
        if (_opening) return _opening;
        _opening = new Promise(function (resolve, reject) {
            var req;
            try { req = indexedDB.open(DB_NAME, DB_VERSION); }
            catch (e) { reject(e); return; }
            req.onupgradeneeded = function () {
                var db = req.result;
                if (!db.objectStoreNames.contains('extractor_state')) db.createObjectStore('extractor_state');
                if (!db.objectStoreNames.contains('creator_drafts')) db.createObjectStore('creator_drafts');
                if (!db.objectStoreNames.contains('inspector_history')) {
                    var s = db.createObjectStore('inspector_history', { keyPath: 'id', autoIncrement: true });
                    s.createIndex('savedAt', 'savedAt');
                }
                if (!db.objectStoreNames.contains('cluster_kv')) db.createObjectStore('cluster_kv');
            };
            req.onsuccess = function () {
                _db = req.result;
                _db.onclose = function () { _db = null; };
                resolve(_db);
                // Fire-and-forget prune
                setTimeout(_prune, 0);
            };
            req.onerror = function () { reject(req.error || new Error('idb-open-failed')); };
            req.onblocked = function () { reject(new Error('idb-blocked')); };
        }).catch(function (e) {
            _opening = null;
            throw e;
        });
        return _opening;
    }

    function _tx(storeName, mode) {
        return _open().then(function (db) {
            return db.transaction(storeName, mode).objectStore(storeName);
        });
    }

    function _prune() {
        try {
            _open().then(function (db) {
                var now = Date.now();
                ['extractor_state', 'creator_drafts'].forEach(function (st) {
                    try {
                        var tx = db.transaction(st, 'readwrite');
                        var s = tx.objectStore(st);
                        var keys = s.getAllKeys();
                        keys.onsuccess = function () {
                            (keys.result || []).forEach(function (k) {
                                var g = s.get(k);
                                g.onsuccess = function () {
                                    var v = g.result;
                                    if (v && v.savedAt && (now - v.savedAt > TTL_RESUME)) {
                                        try { s.delete(k); } catch (_) {}
                                    }
                                };
                            });
                        };
                    } catch (_) {}
                });
                try {
                    var hx = db.transaction('inspector_history', 'readwrite');
                    var hs = hx.objectStore('inspector_history');
                    var ix = hs.index('savedAt');
                    var range = IDBKeyRange.upperBound(now - TTL_HISTORY);
                    var cur = ix.openCursor(range);
                    cur.onsuccess = function () {
                        var c = cur.result;
                        if (c) { try { c.delete(); } catch (_) {} c.continue(); }
                    };
                    // Cap absolute count
                    var count = hs.count();
                    count.onsuccess = function () {
                        var over = count.result - HISTORY_MAX;
                        if (over <= 0) return;
                        var oldest = hs.index('savedAt').openCursor(null, 'next');
                        oldest.onsuccess = function () {
                            var c = oldest.result;
                            if (c && over > 0) { try { c.delete(); } catch (_) {} over--; c.continue(); }
                        };
                    };
                } catch (_) {}
            }).catch(function () {});
        } catch (_) {}
    }

    function putNow(store, key, value) {
        if (!_supported) return Promise.resolve(false);
        return _tx(store, 'readwrite').then(function (s) {
            return new Promise(function (resolve) {
                try {
                    var record = (typeof value === 'object' && value !== null && !Array.isArray(value))
                        ? Object.assign({}, value, { savedAt: Date.now() })
                        : { value: value, savedAt: Date.now() };
                    var req = s.put(record, key);
                    req.onsuccess = function () { resolve(true); };
                    req.onerror = function () { resolve(false); };
                } catch (_) {
                    resolve(false);
                }
            });
        }).catch(function () { return false; });
    }

    function put(store, key, value) {
        if (!_supported) return Promise.resolve(false);
        var dk = store + '|' + key;
        if (_debouncers.has(dk)) clearTimeout(_debouncers.get(dk));
        return new Promise(function (resolve) {
            var t = setTimeout(function () {
                _debouncers.delete(dk);
                putNow(store, key, value).then(resolve);
            }, DEBOUNCE_MS);
            _debouncers.set(dk, t);
        });
    }

    function get(store, key) {
        if (!_supported) return Promise.resolve(null);
        return _tx(store, 'readonly').then(function (s) {
            return new Promise(function (resolve) {
                try {
                    var req = s.get(key);
                    req.onsuccess = function () {
                        var v = req.result;
                        if (!v) { resolve(null); return; }
                        if (v.savedAt && (store === 'extractor_state' || store === 'creator_drafts')) {
                            if (Date.now() - v.savedAt > TTL_RESUME) { resolve(null); return; }
                        }
                        resolve(v);
                    };
                    req.onerror = function () { resolve(null); };
                } catch (_) {
                    resolve(null);
                }
            });
        }).catch(function () { return null; });
    }

    function del(store, key) {
        if (!_supported) return Promise.resolve(false);
        return _tx(store, 'readwrite').then(function (s) {
            return new Promise(function (resolve) {
                try {
                    var req = s.delete(key);
                    req.onsuccess = function () { resolve(true); };
                    req.onerror = function () { resolve(false); };
                } catch (_) {
                    resolve(false);
                }
            });
        }).catch(function () { return false; });
    }

    function append(store, record) {
        if (!_supported) return Promise.resolve(null);
        return _tx(store, 'readwrite').then(function (s) {
            return new Promise(function (resolve) {
                try {
                    var rec = Object.assign({}, record, { savedAt: Date.now() });
                    var req = s.add(rec);
                    req.onsuccess = function () { resolve(req.result); };
                    req.onerror = function () { resolve(null); };
                } catch (_) {
                    resolve(null);
                }
            });
        }).catch(function () { return null; });
    }

    function list(store, limit) {
        if (!_supported) return Promise.resolve([]);
        return _tx(store, 'readonly').then(function (s) {
            return new Promise(function (resolve) {
                try {
                    var out = [];
                    var ix = s.indexNames && s.indexNames.contains('savedAt') ? s.index('savedAt') : null;
                    var cur = ix ? ix.openCursor(null, 'prev') : s.openCursor(null, 'prev');
                    cur.onsuccess = function () {
                        var c = cur.result;
                        if (!c || (limit && out.length >= limit)) { resolve(out); return; }
                        out.push(c.value);
                        c.continue();
                    };
                    cur.onerror = function () { resolve(out); };
                } catch (_) {
                    resolve([]);
                }
            });
        }).catch(function () { return []; });
    }

    function clearStore(store) {
        if (!_supported) return Promise.resolve(false);
        return _tx(store, 'readwrite').then(function (s) {
            return new Promise(function (resolve) {
                try {
                    var req = s.clear();
                    req.onsuccess = function () { resolve(true); };
                    req.onerror = function () { resolve(false); };
                } catch (_) {
                    resolve(false);
                }
            });
        }).catch(function () { return false; });
    }

    function clearAll() {
        return Promise.all(STORES.map(function (st) { return clearStore(st); }));
    }

    function probe() {
        if (!_supported) return Promise.resolve({ ok: false, reason: 'no-idb' });
        return putNow('cluster_kv', '__probe', { v: 1 }).then(function (ok) {
            return ok ? { ok: true } : { ok: false, reason: 'put-failed' };
        });
    }

    w.__DiskImage.Persistence = {
        open: _open,
        put: put,
        putNow: putNow,
        get: get,
        delete: del,
        append: append,
        list: list,
        clearStore: clearStore,
        clearAll: clearAll,
        probe: probe,
        STORES: STORES,
        TTL_RESUME: TTL_RESUME,
        TTL_HISTORY: TTL_HISTORY,
        HISTORY_MAX: HISTORY_MAX
    };
})(window);
